import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'

// FLIPR Logo Icon
const FLIPRLogo = ({ size = 80, animated = true }: { size?: number; animated?: boolean }) => (
  <img
    src="/icon.png"
    alt="FLIPR"
    width={size}
    height={size}
    style={{
      borderRadius: '20%',
      ...(animated ? { animation: 'logoFloat 3s ease-in-out infinite' } : {}),
    }}
  />
)

const Signup = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
  })
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const API_URL = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:8000'
      const response = await axios.post(`${API_URL}/api/auth/signup/`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      // Armazenar tokens
      localStorage.setItem('access_token', response.data.tokens.access)
      localStorage.setItem('refresh_token', response.data.tokens.refresh)

      // Redirecionar para o dashboard
      navigate('/')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao criar conta')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#0f0f23',
    }}>
      {/* Left Panel - Branding */}
      <div style={{
        flex: 1,
        display: 'none',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 'var(--space-12)',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }} className="signup-left-panel">
        {/* Animated background shapes */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'pulse 6s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          animation: 'pulse 8s ease-in-out infinite reverse',
        }} />

        {/* Grid pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }} />

        {/* Main content */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
        }}>
          <img
            src="/logo-white.png"
            alt="FLIPR CRM"
            style={{
              height: '96px',
              marginTop: 'var(--space-6)',
              marginBottom: 'var(--space-4)',
            }}
          />

          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255,255,255,0.6)',
            maxWidth: '400px',
            lineHeight: 1.6,
          }}>
            Comece grátis com 30 dias de trial no plano Growth
          </p>

          {/* Benefits */}
          <div style={{
            marginTop: 'var(--space-12)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
          }}>
            {[
              { icon: '✨', text: '30 dias grátis' },
              { icon: '📊', text: '10.000 leads inclusos' },
              { icon: '👥', text: 'Até 10 usuários' },
              { icon: '🤖', text: '15 automações com IA' },
              { icon: '💬', text: 'Atendimento multicanal' },
              { icon: '🚀', text: 'Sem cartão de crédito' },
            ].map((benefit, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`,
                }}
              >
                <span style={{ fontSize: '1.25rem' }}>{benefit.icon}</span>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
                  {benefit.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Signup Form */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-8)',
        background: 'linear-gradient(180deg, #0f0f23 0%, #1a1a2e 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Subtle background effects */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-50%',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(6, 182, 212, 0.05) 0%, transparent 50%)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-50%',
          left: '-50%',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.05) 0%, transparent 50%)',
        }} />

        <div style={{
          width: '100%',
          maxWidth: '420px',
          position: 'relative',
          zIndex: 1,
        }}>
          {/* Mobile logo */}
          <div style={{ textAlign: 'center', marginBottom: 'var(--space-8)' }} className="mobile-logo">
            <img
              src="/logo-white.png"
              alt="FLIPR CRM"
              style={{ height: '72px', marginTop: 'var(--space-3)' }}
            />
          </div>

          {/* Welcome text */}
          <div style={{ marginBottom: 'var(--space-8)' }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: 'var(--space-2)',
            }}>
              Crie sua conta
            </h2>
            <p style={{
              fontSize: '1rem',
              color: 'rgba(255,255,255,0.5)',
            }}>
              Comece grátis, sem cartão de crédito
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '12px',
              padding: 'var(--space-4)',
              marginBottom: 'var(--space-6)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              animation: 'shake 0.5s ease-out',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p style={{ fontSize: '0.875rem', color: '#f87171', margin: 0 }}>
                {error}
              </p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
              {/* Name field */}
              <div>
                <label
                  htmlFor="name"
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'rgba(255,255,255,0.7)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Nome completo
                </label>
                <div style={{ position: 'relative' }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={focusedField === 'name' ? '#06b6d4' : 'rgba(255,255,255,0.4)'}
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      transition: 'stroke 0.2s',
                    }}
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Seu nome completo"
                    style={{
                      width: '100%',
                      padding: '14px 16px 14px 48px',
                      fontSize: '0.95rem',
                      background: focusedField === 'name' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(255,255,255,0.05)',
                      border: focusedField === 'name' ? '1px solid rgba(6, 182, 212, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#ffffff',
                      transition: 'all 0.2s',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* Email field */}
              <div>
                <label
                  htmlFor="email"
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'rgba(255,255,255,0.7)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Email
                </label>
                <div style={{ position: 'relative' }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={focusedField === 'email' ? '#06b6d4' : 'rgba(255,255,255,0.4)'}
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      transition: 'stroke 0.2s',
                    }}
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="seu@email.com"
                    style={{
                      width: '100%',
                      padding: '14px 16px 14px 48px',
                      fontSize: '0.95rem',
                      background: focusedField === 'email' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(255,255,255,0.05)',
                      border: focusedField === 'email' ? '1px solid rgba(6, 182, 212, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#ffffff',
                      transition: 'all 0.2s',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* Company field */}
              <div>
                <label
                  htmlFor="company"
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'rgba(255,255,255,0.7)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Nome da empresa
                </label>
                <div style={{ position: 'relative' }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={focusedField === 'company' ? '#06b6d4' : 'rgba(255,255,255,0.4)'}
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      transition: 'stroke 0.2s',
                    }}
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    onFocus={() => setFocusedField('company')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Nome da sua empresa"
                    style={{
                      width: '100%',
                      padding: '14px 16px 14px 48px',
                      fontSize: '0.95rem',
                      background: focusedField === 'company' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(255,255,255,0.05)',
                      border: focusedField === 'company' ? '1px solid rgba(6, 182, 212, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#ffffff',
                      transition: 'all 0.2s',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* Password field */}
              <div>
                <label
                  htmlFor="password"
                  style={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: 'rgba(255,255,255,0.7)',
                    marginBottom: 'var(--space-2)',
                  }}
                >
                  Senha
                </label>
                <div style={{ position: 'relative' }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={focusedField === 'password' ? '#06b6d4' : 'rgba(255,255,255,0.4)'}
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      transition: 'stroke 0.2s',
                    }}
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    placeholder="Digite sua senha"
                    style={{
                      width: '100%',
                      padding: '14px 48px 14px 48px',
                      fontSize: '0.95rem',
                      background: focusedField === 'password' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(255,255,255,0.05)',
                      border: focusedField === 'password' ? '1px solid rgba(6, 182, 212, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      color: '#ffffff',
                      transition: 'all 0.2s',
                      outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                    }}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  background: 'linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)',
                  color: '#ffffff',
                  opacity: isLoading ? 0.7 : 1,
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 20px rgba(6, 182, 212, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-2)',
                  marginTop: 'var(--space-2)',
                }}
              >
                {isLoading ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#ffffff',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite',
                    }} />
                    Criando conta...
                  </>
                ) : (
                  <>
                    Começar grátis
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14"/>
                      <path d="m12 5 7 7-7 7"/>
                    </svg>
                  </>
                )}
              </button>

              {/* Terms */}
              <p style={{
                fontSize: '0.75rem',
                color: 'rgba(255,255,255,0.4)',
                textAlign: 'center',
                margin: 0,
              }}>
                Ao criar uma conta, você concorda com nossos{' '}
                <a href="/terms" style={{ color: '#06b6d4', textDecoration: 'none' }}>
                  Termos de Uso
                </a>{' '}
                e{' '}
                <a href="/privacy" style={{ color: '#06b6d4', textDecoration: 'none' }}>
                  Política de Privacidade
                </a>
              </p>
            </div>
          </form>

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            margin: 'var(--space-8) 0',
          }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>
              ou
            </span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
          </div>

          {/* Login link */}
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
              Já tem uma conta?{' '}
              <Link
                to="/login"
                style={{
                  fontWeight: '600',
                  color: '#06b6d4',
                  textDecoration: 'none',
                }}
              >
                Faça login
              </Link>
            </p>
          </div>

          {/* Footer */}
          <p style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.3)',
            marginTop: 'var(--space-8)',
          }}>
            FLIPR CRM — Transforme leads em clientes
          </p>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.1); }
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }

        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: #ffffff !important;
          -webkit-box-shadow: 0 0 0px 1000px #1a1a2e inset !important;
          transition: background-color 5000s ease-in-out 0s;
          caret-color: #ffffff;
        }

        button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(6, 182, 212, 0.4);
        }

        @media (min-width: 1024px) {
          .signup-left-panel {
            display: flex !important;
          }
          .mobile-logo {
            display: none !important;
          }
        }
      `}</style>
    </div>
  )
}

export default Signup
